/**
 * levelgen/town.ts — Laying out the streets, the houses and the greens.
 *
 * Three passes, in the order a town is actually built:
 *
 *   1. `planStreets`  a grid of blocks with road corridors between them, which
 *                     fixes the size of the map.
 *   2. `placePlots`   building plots inside each block, kept off the footways.
 *   3. `placeParks`   grass on whatever ground is left over.
 *
 * ## Why the streets come first, and why the map is measured from them
 *
 * The tempting order is the other way round — pick a map size, then draw roads
 * across it — and it produces slivers. A road corridor is a fixed
 * `ROADS.WIDTH + 2 × PAVEMENT.WIDTH` wide (the carriageway plus a footway each
 * side), so dropping two of them onto an arbitrary rectangle leaves whatever
 * remains, and "whatever remains" is regularly 40 units of block that no house
 * fits in and no garden can be planted in.
 *
 * So the block sizes are chosen first, from the difficulty budget, and the map
 * is however big the blocks and the corridors between them add up to. Every
 * block is then guaranteed buildable, and the footway width is guaranteed to be
 * the full `PAVEMENT.WIDTH` rather than whatever was left over — which is the
 * promise `pavementClearance.test.ts` exists to keep for the authored town.
 */
import { WALLS, ROOFS, PAINTS } from '../../config/palette';
import { BUILDINGS, LANES, PAVEMENT, ROADS } from '../../config/constants';
import { blockInteriors, freeRects } from '../yards';
import { footwayBands, subtractGaps } from '../pavement';
import { faceGaps, frontFacing, rectsOverlap, type Facing, type Rect } from '../level';
import { carriagewayWidth, spanReaches } from '../roads';
import { bayMinLength, splitFrontage } from '../terrace';
import { ringBlocks, worthPlanting } from './surround';
import type { LevelBudget, Range } from './difficulty';
import type { Rng } from './rng';
import type { PlotSpec, RoadSpec, ToneSpec } from './types';

/** Carriageway plus a footway either side — the full width a road takes up. */
export const ROAD_CORRIDOR = ROADS.WIDTH + PAVEMENT.WIDTH * 2;

/** The streets, and the ground they leave behind. */
export interface StreetPlan {
  /** The whole grid, ring included — everything that gets drawn. */
  world: { width: number; height: number };
  /**
   * The part of it the walk happens on. Everything outside is the surround:
   * drawn, never played. See `levelgen/surround.ts`.
   *
   * The edge falls on the **outer** kerb of the perimeter street, so that street
   * and both its footways are inside the walk and the terrace fronting it from
   * the ring has a reachable pavement to open its doors onto. What physically
   * stops the walker is therefore that terrace, for most of the boundary's
   * length — see `render/world.ts` for the hedge that closes the gaps.
   */
  bounds: Rect;
  roads: RoadSpec[];
  /** One rect per block: the map minus every road corridor. */
  blocks: Rect[];
}

/**
 * Smallest plot worth building on.
 *
 * `BAY_MIN_WIDTH` is the floor for a cottage's *frontage*; a plot also has to be
 * deep enough that the cottage comes out `BAY_MIN_RATIO` deeper than it is
 * wide, because that is what turns its ridge front-to-back and its gable end to
 * the street (see `terrace.ts`). A plot narrower than this either loses its
 * front door or grows a roof facing the camera.
 *
 * Those two are in tension, and this number is where they meet: the shallowest
 * plot has to be at least `BAY_MIN_WIDTH × BAY_MIN_RATIO` deep or the narrowest
 * cottage allowed will not fit on it at the proportion required, and the
 * generator quietly stops building on its shallowest frontages. The margin is
 * thin — 64 against 62.1 — so raising the ratio past `1 + 18 / BAY_MIN_WIDTH`
 * means raising this too. `town.test.ts` pins it.
 */
export const MIN_PLOT = BUILDINGS.BAY_MIN_WIDTH + 18;

/**
 * How far a block edge may be off the pavement and still count as a frontage.
 *
 * Zero in principle — `blockInteriors` cuts a block at the corridor boundary,
 * so an edge with pavement along it is *on* it. A unit of slack absorbs the
 * fractional block sizes a generated town produces without letting a genuinely
 * set-back edge through, which would put a door onto a weed patch.
 */
const FRONTAGE_TOLERANCE = 1;

/**
 * How deep a plot is, front wall to back wall.
 *
 * The floor is `MIN_PLOT + 2` rather than `MIN_PLOT` so that a **single**
 * cottage can stand on a plot at all: a bay has to be narrower than the plot is
 * deep by `BAY_MIN_RATIO` (see `frontageFor`), and a bay also has to reach
 * `BAY_MIN_WIDTH` to be worth building, so a plot at the very floor leaves the
 * one cottage that fits barely any width to be drawn at. Two units of headroom
 * is the difference between a street of pairs and threes and one that can also
 * hold a single cottage.
 */
const PLOT_DEPTH: Range = [MIN_PLOT + 2, MIN_PLOT + 64];

/** How much street one plot takes up along its frontage. */
const PLOT_FRONTAGE: Range = [MIN_PLOT, MIN_PLOT + 48];

/**
 * How deep a plot fronting a **vertical** street may be.
 *
 * Every ridge in the town has to run north–south. The camera looks north and
 * down at 58°, so a ridge running east–west turns one of its slopes to face it
 * and that slope renders as a single enormous featureless quad — the constraint
 * the whole roof design is built around (see AGENTS.md, and `gableSlope`, which
 * runs the ridge along the longer side).
 *
 * On a north or south frontage that comes nearly free: bays are cut across x,
 * so each one is narrow across and deep back, and the proportion rule only has
 * to cap how wide a bay may get. On an east or west frontage the cut runs the
 * other way and the rule inverts — each bay has to reach `BAY_MIN_RATIO` times
 * the plot's depth *along the street* — so depth here is what every bay is
 * measured against, and a deep cross-street plot needs a very long frontage to
 * carry even one cottage. Capping it is what keeps such a plot buildable on an
 * ordinary block.
 *
 * This used to be paired with a frontage cap that held the plot to a *single*
 * bay, sized just under two `BAY_MIN_WIDTH`s so `bayCount` could not divide it.
 * That cap is gone: `bayMinLength` now tells the cut how long a cross-street
 * bay has to be, so the frontage is free to run on and carry a proper terrace —
 * eaves to the street, under one long ridge — instead of one square house.
 */
const CROSS_DEPTH_MAX = BUILDINGS.BAY_MIN_WIDTH * 2 - 12;

/**
 * How much longer than its minimum a cross-street bay may be drawn.
 *
 * The minimum is already `BAY_MIN_RATIO` times the depth, so this is variety
 * rather than proportion: a little over one, because the ratio is what the look
 * rests on and a bay stretched to twice it is a barn.
 */
const CROSS_BAY_STRETCH = 1.3;

/**
 * Side passages and back alleys between neighbouring plots.
 *
 * The minimum is not a look decision. A gap is walkable ground, and the walker
 * is `PLAYER.RADIUS * 2` across with a dog on a lead beside them — so a passage
 * narrower than that is a dead end that looks like a route, and a ring of plots
 * with only such passages through it can seal a block interior off entirely.
 * Kept comfortably above the diameter rather than at it, because the walkability
 * check the validator runs is on an 8-unit grid and a gap that only just fits
 * reads as blocked half the time.
 */
const PLOT_GAP: Range = [42, 72];

/**
 * The smallest block worth carving out of the town.
 *
 * Two plots along its frontage and the depth to build them. Anything smaller is
 * a strip of ground with a street round it and nothing on it, and a town made
 * of those reads as *more road than town* however organic the street plan is —
 * which is exactly how it looked when lanes and jogs were free to cut a block
 * to any size. Every way that divides a block checks this before dividing it.
 */
const MIN_BLOCK_ALONG = MIN_PLOT * 2 + PLOT_GAP[0];
const MIN_BLOCK_DEEP = PLOT_DEPTH[0] + PLOT_GAP[0];

/** The couple of houses that make a block worth having. */
const BLOCK_MIN_HOUSES = 2;

/** Whether a block can hold the couple of houses that make it worth having. */
export function blockIsWorthIt(block: Rect): boolean {
  return (
    Math.min(block.w, block.h) >= MIN_BLOCK_DEEP &&
    Math.max(block.w, block.h) >= MIN_BLOCK_ALONG
  );
}

/**
 * Lay out the street grid, and with it the size of the map.
 *
 * Block sizes are drawn per column and per row rather than per block, so the
 * streets run straight from one side of the map to the other. That is a
 * constraint from the rest of the codebase rather than a style choice:
 * `ROADS.SEGMENTS` describes roads that span the whole map, and the junction,
 * footway and traffic geometry all derive from that.
 */
export function planStreets(rng: Rng, budget: LevelBudget, ring: Rng | null = null): StreetPlan {
  const cols = rng.int(...budget.cols);
  const rows = rng.int(...budget.rows);
  const playCols = Array.from({ length: cols }, () => rng.int(...budget.blockWidth));
  const playRows = Array.from({ length: rows }, () => rng.int(...budget.blockHeight));

  // The surround is drawn on its own substream, and drawn *before* the grid is
  // laid out rather than bolted on afterwards. Its blocks are simply more
  // entries in the same column and row lists, which is what makes the streets
  // run straight through from the ring, across the town, and out the far side —
  // a ring with its own street plan would meet the town's at every edge.
  const west = ring ? ringBlocks(ring, budget.blockWidth) : [];
  const east = ring ? ringBlocks(ring, budget.blockWidth) : [];
  const north = ring ? ringBlocks(ring, budget.blockHeight) : [];
  const south = ring ? ringBlocks(ring, budget.blockHeight) : [];

  const colWidths = [...west, ...playCols, ...east];
  const rowHeights = [...north, ...playRows, ...south];

  // **Widths are chosen before the grid is measured, not after.** The map is
  // measured from the streets (see the note at the top of this file), and a
  // street's corridor is now its own width plus two footways — so a town whose
  // high street was widened afterwards would have every block east of it shifted
  // by 32 units and every plot on it hanging over the kerb.
  const vWidths = streetWidths(rng, colWidths.length - 1, west.length, playCols.length, budget);
  const hWidths = streetWidths(rng, rowHeights.length - 1, north.length, playRows.length, budget);

  const across = layOut(colWidths, vWidths);
  const down = layOut(rowHeights, hWidths);
  const world = { width: across.extent, height: down.extent };

  const roads: RoadSpec[] = [
    ...across.roads.map((r): RoadSpec => street('v', r)),
    ...down.roads.map((r): RoadSpec => street('h', r)),
  ];

  // Derived with the same function `render/world.ts` uses to lay the gardens,
  // rather than re-deriving them from the cursor above. The two must agree
  // about where a block starts or the planting ends up on the pavement.
  const blocks = blockInteriors(world, roads, ROADS.WIDTH, PAVEMENT.WIDTH, 0);

  // The walk starts where the ring's last block ends — the outer kerb of the
  // perimeter street, so that street and both its footways are inside it.
  const bounds: Rect = {
    x: across.edgeAfter(west.length - 1),
    y: down.edgeAfter(north.length - 1),
    w: across.startOf(west.length + playCols.length) - across.edgeAfter(west.length - 1),
    h: down.startOf(north.length + playRows.length) - down.edgeAfter(north.length - 1),
  };

  return { world, bounds, roads, blocks };
}

/** A road as the layout knows it: where its centreline runs, and how wide. */
interface Laid {
  pos: number;
  width: number;
}

/** Turn a laid-out road into a spec, leaving an ordinary street's width unsaid. */
function street(orient: 'h' | 'v', r: Laid): RoadSpec {
  // Omitted when it is the ordinary width, exactly as `from`/`to`/`kind` are
  // omitted at their defaults — so a town of plain streets produces the same
  // data it always did, and a width in the spec always means something.
  return r.width === ROADS.WIDTH ? { orient, pos: r.pos } : { orient, pos: r.pos, width: r.width };
}

/**
 * Walk the blocks and the corridors between them, once, and remember where
 * everything landed.
 *
 * One sweep rather than three separate derivations of the same cursor. The map
 * extent, the road centrelines and the boundary all have to agree about where a
 * block starts, and with corridors of different widths they will only agree if
 * they are read off the same walk.
 */
function layOut(
  blockSizes: readonly number[],
  widths: readonly number[],
): {
  extent: number;
  roads: Laid[];
  /** Where block `i` begins. */
  startOf: (i: number) => number;
  /** Where block `i` ends — the near edge of the corridor beyond it. */
  edgeAfter: (i: number) => number;
} {
  const starts: number[] = [];
  const roads: Laid[] = [];
  let cursor = 0;

  for (let i = 0; i < blockSizes.length; i++) {
    starts.push(cursor);
    cursor += blockSizes[i];
    if (i === blockSizes.length - 1) break;
    const corridor = corridorFor(widths[i]);
    roads.push({ pos: cursor + corridor / 2, width: widths[i] });
    cursor += corridor;
  }

  return {
    extent: cursor,
    roads,
    startOf: (i) => (i < starts.length ? starts[i] : cursor),
    edgeAfter: (i) => (i < 0 ? 0 : starts[i] + blockSizes[i]),
  };
}

/** How much ground a street of this width takes up: carriageway plus footways. */
export function corridorFor(width: number): number {
  return width + PAVEMENT.WIDTH * 2;
}

/**
 * Choose a width for every street down one axis.
 *
 * **One high street each way**, and it is the middle-most street of the
 * playable town — the one a player would call the high street, and the one
 * `windStreets` already keeps whole as the spine. Making it literally wider is
 * what turns that from an internal fact about the generator into something the
 * player can see and navigate by.
 *
 * The rest are ordinary streets, some of them narrowed to back lanes. Only
 * *narrowed*: a second wide street would leave the town with two high streets
 * and no hierarchy, which reads as no decision having been made at all.
 *
 * Note a back street is easier to cross rather than harder — it is less tarmac
 * between the footways. The pressure a wide high street adds is real and is
 * accounted for in `crossingWindowMs`; the back lanes are variety, and are what
 * stop the widening reading as one odd street in a uniform grid.
 */
function streetWidths(
  rng: Rng,
  count: number,
  ringBefore: number,
  playable: number,
  budget: LevelBudget,
): number[] {
  if (count <= 0) return [];

  // Candidates are the streets bounding or inside the playable town — the ring's
  // own streets are out in the fog, and a high street nobody can walk down is a
  // wide road rather than a high street. Indices `ringBefore - 1` (the near
  // perimeter street) through `ringBefore + playable - 1` (the far one).
  const first = Math.max(0, ringBefore - 1);
  const last = Math.min(count - 1, ringBefore + playable - 1);
  const high = Math.round((first + last) / 2);

  return Array.from({ length: count }, (_, i) => {
    if (i === high) return ROADS.WIDTHS.HIGH;
    return rng.chance(budget.backStreetShare) ? ROADS.WIDTHS.BACK : ROADS.WIDTHS.STREET;
  });
}

/**
 * How far sideways a street steps when it jogs.
 *
 * Below the minimum the two halves read as one street drawn badly rather than
 * as a street that bends; the maximum is only a ceiling, since the real limit
 * is whatever room the neighbouring streets leave.
 */
const JOG: Range = [ROAD_CORRIDOR * 0.7, ROAD_CORRIDOR * 2];

/**
 * Make the streets wind: jog them sideways, and let some of them stop.
 *
 * A stage of its own on its own substream, between the grid and the lanes.
 *
 * Two moves, and both of them hang off an existing junction rather than
 * happening anywhere:
 *
 * - **A jog** splits a street into two collinear segments either side of a
 *   cross street, the far half stepped sideways. That is a *staggered
 *   crossroads*, and hanging it off a junction is what keeps the network
 *   joined up — the cross street is the piece you drive along to get from one
 *   half to the other. Offset anywhere else and the two halves simply do not
 *   meet.
 * - **A stub** ends a street at a junction, or a little past one, leaving a
 *   T-junction or a cul-de-sac. This is what makes blocks uneven: a street that
 *   stops divides nothing past its end, so the two blocks it would have
 *   separated arrive as one long one.
 *
 * **One street each way is left whole.** Not a style choice: only a street that
 * runs clean across the map may carry traffic (a car has to come from somewhere
 * and go somewhere), so without a spine each way a town would have no traffic in
 * it at all and nothing for the crossing count to measure.
 */
export function windStreets(rng: Rng, plan: StreetPlan, budget: LevelBudget): StreetPlan {
  // **One orientation at a time, and each against the other as it finally is.**
  //
  // Deciding both from the original grid is wrong, and wrong in the way that is
  // hardest to see: a street jogs at a cross street, that cross street is then
  // shortened away from it, and the two halves of the jog no longer meet. What
  // is left is not a staggered crossroads but two streets ending in open
  // ground — a break in the network, and the thing that looks most obviously
  // unfinished on screen.
  //
  // So the horizontals are wound first, against verticals that are all still
  // full-map and therefore certain to be crossed; then the verticals are wound
  // against the horizontals as they now stand, taking only the crossings that
  // genuinely reach them.
  let roads: RoadSpec[] = [...plan.roads];
  // Positions the streets wound so far now **end on**. Winding one of those
  // would move it out from under the end that relies on it, and the second
  // pass would quietly undo the first — which is how two halves of a jog end
  // up meeting at a cross street that is no longer there.
  const loadBearing = new Set<number>();

  for (const orient of ['h', 'v'] as const) {
    const same = roads.filter((r) => r.orient === orient);
    const cross = roads.filter((r) => r.orient !== orient);
    const across = orient === 'h' ? plan.world.height : plan.world.width;
    const along = orient === 'h' ? plan.world.width : plan.world.height;

    // The spine is whichever runs nearest the middle of the town — the one a
    // player would call the high street, and the one most worth keeping whole.
    const spine = same.reduce(
      (best, r) => (Math.abs(r.pos - across / 2) < Math.abs(best.pos - across / 2) ? r : best),
      same[0],
    );

    // **Positions this orientation has actually claimed**, updated as each
    // street is wound rather than read off the grid it started from.
    //
    // Deciding every jog against the original layout is wrong in a way that only
    // shows up once a town has streets enough to be worth winding: a street at
    // 500 steps up to 700 and one at 900 steps down to 700, each of them
    // perfectly clear of where the *other used to be*, and the two end up in one
    // corridor. Neither move is illegal on its own, which is why checking them
    // one at a time can never catch it.
    //
    // A jogged street claims its new position as well as its old one — the near
    // half is still sitting on the old line — so both go in the list.
    const claimed: Laid[] = same.map((r) => ({ pos: r.pos, width: widthOf(r) }));

    const wound: RoadSpec[] = [];
    for (const road of same) {
      /** Crossings that reach every position this road would occupy. */
      const reaching = (...positions: number[]): number[] =>
        cross
          .filter((o) => positions.every((p) => spanReaches(o, p)))
          .map((o) => o.pos)
          .sort((a, b) => a - b);

      if (road === spine || loadBearing.has(road.pos) || onBoundary(road, plan.bounds)) {
        wound.push(road);
        continue;
      }
      const room = jogRoom(road, claimed, across);
      const jogged = rng.chance(budget.jogChance)
        ? jogAt(rng, road, reaching, along, room)
        : null;
      if (jogged) {
        wound.push(...jogged);
        const moved = jogged[jogged.length - 1];
        claimed.push({ pos: moved.pos, width: widthOf(moved) });
      } else if (rng.chance(budget.stubChance)) {
        wound.push(stubAt(rng, road, reaching(road.pos), along));
      } else {
        wound.push(road);
      }
    }

    // Whatever the new ends landed on is load-bearing from here on.
    for (const r of wound) {
      const [from, to] = [r.from, r.to];
      for (const end of [from, to]) if (end !== undefined && end > 0 && end < along) {
        loadBearing.add(end);
      }
    }
    roads = [...cross, ...wound];
  }

  return {
    world: plan.world,
    bounds: plan.bounds,
    roads,
    blocks: blockInteriors(plan.world, roads, ROADS.WIDTH, PAVEMENT.WIDTH, 0),
  };
}

/**
 * Whether this is one of the four perimeter streets — the ones running along
 * the edge of the walk.
 *
 * **They are never wound**, and that is a structural rule rather than a
 * stylistic one. The boundary is meant to fall on the far kerb of a continuous
 * street, with the ring's terrace built flush against it from the outside: that
 * is what puts a house rather than an invisible wall in front of the walker for
 * most of the boundary's length, and what keeps the ring's blocks and the
 * town's on opposite sides of it.
 *
 * Stub one of these and two blocks that were on opposite sides of the boundary
 * arrive as a single block straddling it. Everything downstream then quietly
 * misreads that block as part of the town: houses get built across the line,
 * trees get planted on ground the walker cannot reach, and the edge of the walk
 * ends up running through the middle of open ground with nothing on it to
 * explain why you cannot go on.
 *
 * A level with no surround has no perimeter streets to protect, and the map's
 * own edges are not roads, so nothing is held back there.
 */
function onBoundary(road: RoadSpec, bounds: Rect): boolean {
  // Its own corridor, not the ordinary one: the perimeter street may be the
  // high street, and measuring it at 64 would fail to recognise it at 96 —
  // after which it becomes eligible for a stub and the boundary falls apart.
  const half = corridorFor(widthOf(road)) / 2;
  const [near, far] =
    road.orient === 'h' ? [bounds.y, bounds.y + bounds.h] : [bounds.x, bounds.x + bounds.w];
  return road.pos === near + half || road.pos === far - half;
}

/** This road's carriageway width, defaulting to the ordinary street. */
const widthOf = (road: RoadSpec): number => carriagewayWidth(road, ROADS.WIDTH);

/**
 * How far a street may step sideways before it lands on a neighbour.
 *
 * The corridor of the nearest parallel street either side, and the map's own
 * edges. Returned as the absolute range the street's position may move to,
 * which is what `roads-too-close` and `road-off-map` will hold it to anyway —
 * better to obey them here than to generate seeds the validator throws away.
 */
function jogRoom(road: RoadSpec, claimed: readonly Laid[], across: number): Range {
  const pos = road.pos;
  const half = corridorFor(widthOf(road)) / 2;
  // Room for a **buildable** block beyond the street, not merely room for its
  // own footways. A jog that lands a street 80 units off the map edge is legal
  // and leaves a strip too shallow to put a house on, which is a slice of dead
  // town in exchange for a bend nobody can see.
  const margin = half + MIN_BLOCK_DEEP;
  let lo = margin;
  let hi = across - margin;

  // And a block's worth of room from the next street along, not merely the two
  // corridors touching. `roads-too-close` is satisfied at exactly one corridor
  // apart, which leaves a strip of nothing between two streets.
  // Half of each corridor plus a buildable block between them — measured from
  // both roads, since a high street eats more of the gap than a back lane.
  for (const other of claimed) {
    if (other.pos === pos) continue;
    const apart = half + corridorFor(other.width) / 2 + MIN_BLOCK_DEEP;
    if (other.pos < pos) lo = Math.max(lo, other.pos + apart);
    else hi = Math.min(hi, other.pos - apart);
  }
  return [lo, hi];
}

/** Split a street at one of its junctions and step the far half sideways. */
function jogAt(
  rng: Rng,
  road: RoadSpec,
  reaching: (...positions: number[]) => number[],
  along: number,
  [lo, hi]: Range,
): RoadSpec[] | null {
  const reach = Math.min(JOG[1], Math.max(hi - road.pos, road.pos - lo));
  if (reach < JOG[0]) return null;

  const step = rng.int(Math.round(JOG[0]), Math.round(reach));
  // Whichever side has the room; if both do, either.
  const up = road.pos + step <= hi;
  const down = road.pos - step >= lo;
  if (!up && !down) return null;
  const moved = road.pos + (up && (!down || rng.chance(0.5)) ? step : -step);

  // The cross street the jog hangs off has to reach **both** halves, or the
  // two never meet and the jog is a break in the network rather than a
  // staggered crossroads. Both halves also have to be worth calling a street.
  const usable = reaching(road.pos, moved).filter(
    (c) => c >= ROAD_CORRIDOR * 1.5 && c <= along - ROAD_CORRIDOR * 1.5,
  );
  if (usable.length === 0) return null;
  const at = rng.pick(usable);

  return [
    { ...road, from: 0, to: at },
    { ...road, pos: moved, from: at, to: along },
  ];
}

/**
 * End a street **at** one of its junctions.
 *
 * Exactly at, never past it. A street that runs on and stops in open ground is
 * a road going nowhere: tarmac ending in a blank turning head with no reason to
 * walk down it. Ending at a junction still breaks the grid — the blocks past
 * the end stop being divided, which is where uneven blocks come from — while
 * every street in the town still leads somewhere.
 */
function stubAt(rng: Rng, road: RoadSpec, crossings: readonly number[], along: number): RoadSpec {
  const fromLowEnd = rng.chance(0.5);
  // Keep more than half the street: past that it stops reading as a street that
  // ends and starts reading as a street somebody forgot to draw.
  const usable = crossings.filter((c) =>
    fromLowEnd ? c >= along * 0.55 : c <= along * 0.45,
  );
  if (usable.length === 0) return road;

  const junction = fromLowEnd ? Math.min(...usable) : Math.max(...usable);
  return fromLowEnd ? { ...road, to: junction } : { ...road, from: junction };
}

/**
 * How big a block has to get before a lane can be cut through it.
 *
 * A terrace either side at the minimum depth, a back alley's worth of gap, and
 * the lane itself. Below this the split would leave a strip too thin to build
 * on, which is a cobbled alley running between two empty yards.
 */
const LANE_SPLIT_MIN = MIN_PLOT * 2 + PLOT_GAP[0] + LANES.WIDTH;

/** How far off centre a lane may be cut, as a fraction of the block. */
const LANE_OFFSET = 0.12;

/**
 * Cut cobbled pedestrian lanes through the blocks that are big enough to have a
 * middle nobody can build on.
 *
 * A stage of its own, after the streets, with its own RNG substream — adding
 * these rolls to the street stream would shift the sequence and silently
 * rebuild every town in every test.
 *
 * The lane earns its place twice over. It is a piece of town no grid produces,
 * and it turns the dead centre of a big block into two more **frontages**: once
 * houses may only be built where there is pavement to open onto, an alley is
 * the only way the inside of a block gets built on at all.
 *
 * It spans its block exactly, so both ends come out on the footway of the
 * street either side rather than stopping in somebody's garden.
 */
export function cutLanes(rng: Rng, plan: StreetPlan, budget: LevelBudget): StreetPlan {
  const lanes: RoadSpec[] = [];

  for (const block of plan.blocks) {
    // Never in the surround. A lane earns its place by being a route and by
    // turning the dead middle of a block into somewhere houses can front onto —
    // and out in the ring the first of those is worth nothing, because nobody
    // can walk down it. Cutting them there would buy a little more scenery in
    // the fog at the price of two more frontages' worth of town to build on
    // every ring block in every level.
    if (!rectsOverlap(block, plan.bounds)) continue;

    // Split across whichever axis has the most ground out of reach; a block
    // long in one direction wants its alley running along that direction.
    const splitY = block.h >= block.w;
    const across = splitY ? block.h : block.w;
    if (across < LANE_SPLIT_MIN) continue;
    if (!rng.chance(budget.laneChance)) continue;

    const from = splitY ? block.y : block.x;
    const jitter = Math.floor(across * LANE_OFFSET);
    const pos = Math.round(from + across / 2 + rng.int(-jitter, jitter));

    // Both halves have to be worth having. An alley that leaves a strip of
    // ground too small to build on has not made a lane through a terrace, it
    // has made a lane through nothing — and two of those turn one full block
    // into three empty ones.
    const near = pos - LANES.WIDTH / 2 - from;
    const far = from + across - (pos + LANES.WIDTH / 2);
    const halves = splitY
      ? [{ ...block, h: near }, { ...block, y: pos + LANES.WIDTH / 2, h: far }]
      : [{ ...block, w: near }, { ...block, x: pos + LANES.WIDTH / 2, w: far }];
    if (!halves.every(blockIsWorthIt)) continue;

    lanes.push({
      orient: splitY ? 'h' : 'v',
      pos,
      from: splitY ? block.x : block.y,
      to: splitY ? block.x + block.w : block.y + block.h,
      kind: 'lane',
    });
  }

  if (lanes.length === 0) return plan;
  const roads = [...plan.roads, ...lanes];
  return {
    world: plan.world,
    bounds: plan.bounds,
    roads,
    blocks: blockInteriors(plan.world, roads, ROADS.WIDTH, PAVEMENT.WIDTH, 0),
  };
}

/**
 * Fill the blocks with building plots.
 *
 * ## Houses are laid along the streets, not scattered in the blocks
 *
 * This used to cut a block's **interior** into a grid of cells — inset by a
 * random setback, split into bands and slots, then each plot shrunk and
 * jittered inside its cell. Every one of those steps moves a house away from
 * the street, and together they meant a plot could end up floating in the
 * middle of a block with rough ground on all four sides. Measured over twenty
 * generated towns, **every single house in the town had its front door opening
 * onto open ground**, the worst of them 207 units from the nearest pavement.
 *
 * So plots are now laid along the block's **frontages** — the edges that have
 * pavement running along them — and built flush to that edge, which is exactly
 * the footway edge because `blockInteriors` cuts at the corridor boundary. A
 * house exists only where there is a street for it to open onto.
 *
 * The rotation the layout used to need falls out of this rather than being a
 * step of its own: a plot on a north or south frontage is wide and shallow, one
 * on an east or west frontage is narrow and deep, so the frontage always runs
 * along the street and the ridge always runs back from it.
 *
 * A slot is built on with probability `buildChance`; the ones that come up
 * empty are the gaps in the terrace. Those matter more than they sound — a
 * block built out solid has no interior, which is the exact failure
 * `systems/yards.ts` was written to fix.
 *
 * `reserved` is the ground the bin, the home pad and the walker's start have
 * already claimed (see `landmarks.ts` for why they go first). A slot that runs
 * into it is **shortened along its frontage**, never pulled back off the
 * street, and never discarded — see `trimAlong` for what each of those
 * alternatives cost when they were tried.
 */
export function placePlots(
  rng: Rng,
  plan: StreetPlan,
  budget: LevelBudget,
  reserved: readonly Rect[] = [],
): PlotSpec[] {
  const out: PlotSpec[] = [];
  // Where the pavement actually is. Hoisted, because every plot asks the same
  // question of it and `footwayBands` walks every road in the town to answer.
  const footways = footwayBands(plan.world, plan.roads, ROADS.WIDTH, PAVEMENT.WIDTH);

  for (const block of plan.blocks) {
    const before = out.length;
    // Shuffled so no one frontage always wins the corners where two runs meet.
    const available = rng
      .shuffle(frontages(block, footways))
      .flatMap((front) =>
        runAlong(rng, block, front, reserved).map((rect) => ({ rect, facing: front.facing })),
      );

    for (const slot of available) {
      if (!rng.chance(budget.buildChance)) continue;
      build(slot);
    }

    // Every block that has room gets a **couple** of houses on it, whatever the
    // dice said. One was the old floor and it is not enough: a block with a
    // single cottage marooned in it reads as emptier than one with none, and a
    // town of them reads as more road than town. Anything still short after
    // this is a block the ground genuinely cannot take two houses on — usually
    // one the home pad or the green has most of.
    for (const slot of rng.shuffle(available)) {
      if (out.length - before >= BLOCK_MIN_HOUSES) break;
      build(slot);
    }
  }

  /**
   * Trim a slot clear of everything already spoken for, then build on what is
   * left.
   *
   * Trimmed rather than rejected, and always **along the frontage** — see
   * `trimAlong`. Two runs meeting at a block corner both want the same square,
   * and rejecting the loser outright throws away its entire slot, which on a
   * small block is often its only one. Shortening it instead gives the corner
   * to whichever run got there first and leaves its neighbour a house.
   */
  function build({ rect, facing }: { rect: Rect; facing: Facing }): void {
    const clear = trimAlong(rect, facing, [...reserved, ...out]);
    if (!clear) return;
    // A trimmed plot may come back down to a **single cottage** along its
    // frontage — `BAY_MIN_WIDTH`, not `MIN_PLOT`. Holding it to the full plot
    // minimum is what emptied the cramped blocks in a small town, where the
    // home pad and the bin take most of a frontage between them and what is
    // left either side is one house wide. Depth still has to hold, because
    // that is what keeps the ridge off the camera.
    const alongX = facing === 'north' || facing === 'south';
    const frontage = alongX ? clear.w : clear.h;
    const depth = alongX ? clear.h : clear.w;
    if (frontage < BUILDINGS.BAY_MIN_WIDTH || depth < MIN_PLOT) return;

    // Checked on the finished plot rather than the slot, and against the front
    // it will actually be given. A patio takes its forecourt out of the
    // building's own depth, so the footprint that gets built is shallower than
    // the slot it came from — which can turn a roof to face the camera after
    // the slot itself passed.
    const plot = buildPlot(rng, clear, footways);
    if (!ridgeRunsBack(plot, frontFacing(plot, footways))) return;
    out.push(plot);
  }

  return out;
}

/**
 * Shorten a slot until it clears the reserved ground, or give up on it.
 *
 * Cut **along the frontage only**, never in depth. That restriction is the
 * whole point: the slot's near edge is on the pavement, and pulling it back to
 * dodge the home pad would put its front door in a garden — undoing the one
 * thing this layout exists to guarantee. A terrace shortens to make room for
 * the bin; it does not step back from the street.
 *
 * Discarding the slot outright is not an option either, and this is not a
 * hypothesis: on an easy town a block frontage often holds exactly one slot, so
 * dropping it because a corner touches the home pad emptied whole blocks. Doing
 * it that way took a four-block town from eleven houses down to one.
 */
function trimAlong(slot: Rect, facing: Facing, reserved: readonly Rect[]): Rect | null {
  const alongX = facing === 'north' || facing === 'south';
  let at: Rect = slot;

  for (const keep of reserved) {
    if (!rectsOverlap(at, keep)) continue;

    const from = alongX ? at.x : at.y;
    const to = from + (alongX ? at.w : at.h);
    const blockFrom = alongX ? keep.x : keep.y;
    const blockTo = blockFrom + (alongX ? keep.w : keep.h);

    // The runs left either side of the reservation; keep the longer.
    const before = blockFrom - from;
    const after = to - blockTo;
    const [start, length] = before >= after ? [from, before] : [blockTo, after];
    if (length < MIN_PLOT) return null;

    at = alongX
      ? { ...at, x: start, w: length }
      : { ...at, y: start, h: length };
  }
  return at;
}

/** A block edge with pavement along it, and the depth available behind it. */
interface Frontage {
  facing: Facing;
  /** How far back from the pavement a house here may build. */
  depth: number;
}

/**
 * Which edges of a block a house can front onto.
 *
 * The pivot of the whole layout. Plots used to be cut out of a block's
 * *interior* — inset by a random setback and then jittered inside their cell —
 * so a house could end up floating in the middle of a block with rough ground
 * on all four sides and its door opening onto a weed patch. Here a plot exists
 * only where there is pavement to open onto, and it is built **flush** to that
 * edge, which is exactly the footway edge because `blockInteriors` cuts at the
 * corridor boundary.
 *
 * Depth is capped at just under half the block so two facing terraces leave a
 * back alley between them rather than meeting in the middle.
 */
function frontages(block: Rect, footways: readonly Rect[]): Frontage[] {
  const gaps = faceGaps(block, footways);
  // Flush means flush. A block edge that is not *on* the pavement is either the
  // map boundary or a break in the paving, and neither is a frontage.
  const paved = (facing: Facing): boolean => gaps[facing] <= FRONTAGE_TOLERANCE;

  const out: Frontage[] = [];
  for (const facing of ['north', 'south', 'east', 'west'] as const) {
    if (!paved(facing)) continue;
    const across = facing === 'north' || facing === 'south' ? block.h : block.w;

    // Room is only halved when there is a terrace coming the other way. Halving
    // it unconditionally is what made a lane cost more houses than it created:
    // splitting a block leaves two shallow halves, each with pavement on one
    // side and the map edge or a garden on the other, and reserving space for a
    // facing row that was never coming took the depth below `MIN_PLOT` and left
    // them with no frontage at all.
    const room = paved(opposite(facing)) ? (across - PLOT_GAP[0]) / 2 : across - PLOT_GAP[0];
    const alongX = facing === 'north' || facing === 'south';
    const cap = alongX ? PLOT_DEPTH[1] : CROSS_DEPTH_MAX;
    const depth = Math.floor(Math.min(cap, room));
    if (depth >= MIN_PLOT) out.push({ facing, depth });
  }
  return out;

  function opposite(facing: Facing): Facing {
    return facing === 'north'
      ? 'south'
      : facing === 'south'
        ? 'north'
        : facing === 'east'
          ? 'west'
          : 'east';
  }
}

/**
 * Lay a row of plots along one frontage, flush to the street.
 *
 * Widths and gaps are both drawn per plot rather than dividing the frontage
 * into equal parts. Equal parts is what made a street read as a fence: every
 * house the same width, every gap the same, repeated to the end of the block.
 * Walking a cursor along with a fresh roll each time gives a row of houses of
 * different widths with passages of different widths between them, which is
 * what an English street actually looks like from above.
 *
 * The row also starts at a random offset, so two terraces facing each other
 * across a street don't line up door for door.
 */
function runAlong(rng: Rng, block: Rect, front: Frontage, reserved: readonly Rect[]): Rect[] {
  const alongX = front.facing === 'north' || front.facing === 'south';
  const start = alongX ? block.x : block.y;
  const length = alongX ? block.w : block.h;

  // Depth is drawn once per frontage, so one terrace holds a building line
  // rather than every house stepping in and out at random.
  const depth = rng.int(Math.min(PLOT_DEPTH[0], front.depth), front.depth);
  const near =
    front.facing === 'north'
      ? block.y
      : front.facing === 'south'
        ? block.y + block.h - depth
        : front.facing === 'west'
          ? block.x
          : block.x + block.w - depth;

  // The strip this row will occupy, so the ground already spoken for can be cut
  // out of the row *before* it is laid rather than trimmed off each plot after.
  // The difference is the whole row: the bin and the home pad are nearly as big
  // as a small town's block, and a row that meets one head-on loses its single
  // slot to the trim and the block comes out empty. Stepping over the
  // reservation instead puts a terrace on whatever frontage is left either side.
  const band: Rect = alongX
    ? { x: start, y: near, w: length, h: depth }
    : { x: near, y: start, w: depth, h: length };
  const gaps = reserved
    .filter((r) => rectsOverlap(r, band))
    .map((r) => (alongX ? { from: r.x, to: r.x + r.w } : { from: r.y, to: r.y + r.h }));

  const out: Rect[] = [];
  for (const stretch of subtractGaps(start + length, gaps, start)) {
    const end = stretch.to;
    let cursor =
      stretch.from + rng.int(0, Math.min(PLOT_GAP[0], Math.max(0, end - stretch.from - MIN_PLOT)));

    while (cursor + BUILDINGS.BAY_MIN_WIDTH <= end) {
      const width = frontageFor(rng, depth, alongX, end - cursor);
      if (width === null) break;

      out.push(
        alongX
          ? { x: cursor, y: near, w: width, h: depth }
          : { x: near, y: cursor, w: depth, h: width },
      );
      cursor += width + rng.int(...PLOT_GAP);
    }
  }
  return out;
}

/**
 * How much street one plot takes, given how deep it is and which way it faces.
 *
 * This is where the **ridge rule** is kept, and it is the one piece of the
 * layout that is not symmetric between the two orientations.
 *
 * Every ridge in the town has to run north–south. The camera looks north and
 * down at 58°, so a ridge running east–west turns one of its slopes to face it,
 * and that slope renders as a single enormous featureless quad — the constraint
 * the entire roof design exists to duck (see AGENTS.md). `gableSlope` runs the
 * ridge along a cottage's **longer** side, so the rule reduces to: every bay
 * must come out `BAY_MIN_RATIO` deeper north–south than it is wide east–west.
 *
 * The ratio rather than the bare inequality, because the bare inequality is
 * satisfied by a cottage 78 wide and 79 deep, and that is a square house with a
 * square roof — ridged the right way, reading as ridged no way at all.
 *
 * On a north or south frontage the bays are cut across x, so each is
 * `width / bays` wide and `depth` deep: the bay has to be **narrower** than the
 * plot is deep, by that ratio. On an east or west frontage the cut runs the
 * other way, so each bay is `depth` wide and `width / bays` deep: the bay has
 * to be **longer** than the plot is deep, by the same ratio. Opposite
 * conditions, from one rule — `bayMinLength` is where that inversion lives.
 *
 * Either way the terrace is built by picking one bay and multiplying up, and
 * either way `bayCount` can only divide the result *further*, which on both
 * orientations moves the bay away from square rather than toward it. That is
 * what lets three separate places re-derive the same cut and agree.
 */
function frontageFor(rng: Rng, depth: number, alongX: boolean, room: number): number | null {
  // The widest one cottage may be, from the proportion rule. On a north or
  // south frontage this is a ceiling on the bay; on a cross street it is the
  // floor, because there the frontage is what runs back from the street.
  const widest = Math.floor(depth / BUILDINGS.BAY_MIN_RATIO);

  if (alongX) {
    const cap = Math.min(widest, PLOT_FRONTAGE[1]);
    if (cap < BUILDINGS.BAY_MIN_WIDTH) return null;

    const bay = rng.int(BUILDINGS.BAY_MIN_WIDTH, cap);
    // Most bays a terrace of this bay width can have and still fit the gap.
    const bays = Math.min(BUILDINGS.BAY_MAX, Math.floor(room / bay));
    // Start from the longest terrace that fits and walk down, rather than
    // rolling a length. A row of houses under one roofline is what a market
    // town is made of, and `terrace.ts` already gives it party walls and
    // stepped heights; picking the count uniformly filled the streets with
    // detached cottages standing in their own gaps instead.
    for (let n = Math.max(1, bays); n >= 1; n--) {
      const width = n * bay;
      if (width >= BUILDINGS.BAY_MIN_WIDTH && width <= room) return width;
    }
    return null;
  }

  // Cross street: the bays run back from the street rather than across it, so
  // each one has to be at least `BAY_MIN_RATIO` deeper than the plot is wide.
  // That is the same terrace, turned through a right angle — a row of cottages
  // under one long north–south ridge, showing the street its eaves instead of
  // its gables, which is the other half of how a market town is built and the
  // half a vertical street wants.
  //
  // It used to be held to a single bay instead, capped just under two
  // `BAY_MIN_WIDTH`s so `bayCount` could not split it, and given two units more
  // frontage than depth. Two units is not a proportion: every cross-street
  // house came out square, and on most towns that is the majority of them.
  const lo = Math.max(
    MIN_PLOT,
    Math.ceil(bayMinLength(depth, false, BUILDINGS.BAY_MIN_WIDTH, BUILDINGS.BAY_MIN_RATIO)),
  );
  const hi = Math.min(Math.floor(lo * CROSS_BAY_STRETCH), room);
  if (hi < lo) return null;

  // Longest terrace that fits, exactly as above — but without the walk down,
  // because `bay` is drawn no longer than the room left, so the single-bay case
  // always fits and there is nothing to fall back through.
  const bay = rng.int(lo, hi);
  return Math.max(1, Math.min(BUILDINGS.BAY_MAX, Math.floor(room / bay))) * bay;
}

/**
 * Whether every cottage on this plot comes out `BAY_MIN_RATIO` deeper than it
 * is wide.
 *
 * The ridge rule again, re-checked on the finished plot. `frontageFor` picks a
 * frontage that clears it, but two later steps can undo that: trimming
 * shortens a plot along its frontage to dodge what is already on the ground,
 * and a patio takes its forecourt out of the building's own depth. Both happen
 * after the frontage is chosen and neither knows about this.
 *
 * The margin is the point. Held to the bare inequality this passed a cottage 78
 * wide and 79 deep — a square house with a square roof, whose ridge runs the
 * right way and reads as running no way at all.
 */
function ridgeRunsBack(plot: Rect, facing: Facing): boolean {
  const alongX = facing === 'north' || facing === 'south';
  return bays(plot, alongX).every((bay) => bay.h >= bay.w * BUILDINGS.BAY_MIN_RATIO);
}

/** The cottages a finished plot cuts into, on the rule the renderer will use. */
function bays(plot: Rect, alongX: boolean): ReturnType<typeof splitFrontage> {
  return splitFrontage(
    plot,
    alongX,
    bayMinLength(
      alongX ? plot.h : plot.w,
      alongX,
      BUILDINGS.BAY_MIN_WIDTH,
      BUILDINGS.BAY_MIN_RATIO,
    ),
    BUILDINGS.BAY_MAX,
    BUILDINGS.BAY_JITTER,
  );
}

/**
 * Turn a footprint into a plot: how tall it stands, what it's painted, and
 * whether it gets a front patio.
 *
 * The patio is the fiddly part, and the rule is inherited wholesale from the
 * authored town (see the two patio rules above `BUILDINGS.LIST`): it may only
 * go on a **south-facing** front, because at this camera tilt a patio on the
 * north side is drawn underneath the house's own roof and may as well not
 * exist — and the front has to win by more than `PATIO_DEPTH`, because pulling
 * the plot back to make room can otherwise hand the title to a different face
 * and swing the whole terrace round the side.
 */
function buildPlot(rng: Rng, rect: Rect, footways: readonly Rect[]): PlotSpec {
  const patio = wantsPatio(rect, footways) && rng.chance(0.4);
  // Give the forecourt its depth out of the building, not out of the footway.
  const footprint: Rect = patio ? { ...rect, h: rect.h - BUILDINGS.PATIO_DEPTH } : rect;

  const front = frontFacing(footprint, footways);
  const alongX = front === 'north' || front === 'south';
  const count = bays(footprint, alongX).length;

  return {
    ...footprint,
    // High enough that the lowest cottage in a stepped row still clears its
    // upper windows — the facade pass needs `tall - BAY_STEP` to stay above
    // SHOPFRONT_HEIGHT + STOREY_HEIGHT/2 + WINDOW_H/2 + 6.
    tall: rng.int(68, 76),
    patio,
    tones: pickTones(rng, count),
  };
}

/**
 * Whether this plot could take a patio: its south face must be the nearest to a
 * pavement, and by a clear `PATIO_DEPTH`, so pulling the wall back can't make
 * some other face the front.
 */
function wantsPatio(rect: Rect, footways: readonly Rect[]): boolean {
  if (rect.h - BUILDINGS.PATIO_DEPTH < MIN_PLOT) return false;
  const d = faceGaps(rect, footways);
  const others = Math.min(d.north, d.east, d.west);
  return d.south + BUILDINGS.PATIO_DEPTH < others;
}

/**
 * The paint kit for one plot's terrace.
 *
 * Two rules, both from the authored town. Neighbours never share a wall
 * colour — two cottages the same colour either side of a party wall read as one
 * building with a stripe down it — and the materials alternate, because the
 * brick coursing is the main thing telling two similarly coloured cottages
 * apart, so a terrace of all-render or all-brick loses half its separation.
 */
function pickTones(rng: Rng, count: number): ToneSpec[] {
  const walls = rng.shuffle(Object.values(WALLS));
  const roofs = [ROOFS.SLATE, ROOFS.SLATE_DARK, ROOFS.TILE];
  const paints = rng.shuffle(Object.values(PAINTS));
  const brickFirst = rng.chance(0.5);

  return Array.from({ length: count }, (_, i) => {
    const material: ToneSpec['material'] = (i % 2 === 0) === brickFirst ? 'brick' : 'render';
    // Brick walls come from the two brick colours; rendered ones from the rest.
    // Picked off a shuffled list by index so a row never repeats a wall colour.
    const palette =
      material === 'brick'
        ? [WALLS.BRICK, WALLS.BRICK_DEEP]
        : walls.filter((w) => w !== WALLS.BRICK && w !== WALLS.BRICK_DEEP);
    return {
      color: palette[i % palette.length],
      roofColor: roofs[(i + (brickFirst ? 1 : 0)) % roofs.length],
      paint: paints[i % paints.length],
      material,
    };
  });
}

/**
 * Lay the grass.
 *
 * `green` comes from `placeLandmarks` and is **not optional**: a dog will only
 * do its business on grass, so a level with none can never be finished. It goes
 * down first and the rest are extras found in whatever ground is left over.
 *
 * Parks go in **after** the bin, home and the green are placed, and take those
 * as `reserved`. That order is not arbitrary: the home pad is drawn well above
 * the ground plane and only appears at the end of a run, so a park laid under
 * it puts two textured slabs on one patch and they z-fight for the whole of the
 * rest of the walk.
 */
export function placeParks(
  rng: Rng,
  plan: StreetPlan,
  plots: readonly Rect[],
  reserved: readonly Rect[],
  budget: LevelBudget,
  greens: readonly Rect[],
): Rect[] {
  const want = rng.int(...budget.parks);
  const inset = 10;

  // Grass out in the surround is grass the dog can never be walked to, so it
  // would be a park in name only — and `bare-parks` would then warn about
  // nothing growing on a green nobody can stand on. Kept to the same band the
  // planting reaches, which is the band that can actually be seen.
  const candidates = plan.blocks
    .filter((block) => worthPlanting(block, plan.bounds))
    .flatMap((block) => freeRects(block, [...plots, ...reserved], PARK_MIN.h))
    .filter((r) => r.w >= PARK_MIN.w + inset * 2 && r.h >= PARK_MIN.h + inset * 2)
    .map((r) => ({ x: r.x + inset, y: r.y + inset, w: r.w - inset * 2, h: r.h - inset * 2 }));

  // The greens the landmarks reserved ground for, first and unconditionally:
  // they are the parks this town was laid out around (see `Landmarks.greens`).
  const out: Rect[] = [...greens];
  // Then, only if the budget still wants more: the best of whatever the
  // terraces left over. Biggest first rather than shuffled — a green is a
  // *place*, somewhere with a fountain in the middle and a path round it (see
  // `systems/parkFurniture.ts`), and at these sizes the difference between the
  // best patch left in a block and a random one is the difference between a
  // park and a lawn. The shuffle is still what breaks ties between equals.
  for (const park of rng.shuffle(candidates).sort((a, b) => b.w * b.h - a.w * a.h)) {
    if (out.length >= Math.max(1, want)) break;
    if (out.some((p) => rectsOverlap(p, park))) continue;
    out.push(park);
  }
  return out;
}

/**
 * The smallest patch of leftover ground worth laying grass on.
 *
 * Raised from 130 × 120, which was "big enough to hold a tree or two" and
 * looked like exactly that: a square of green between the back fences with
 * nothing on it. A park has to have room for something to *be* in it — the
 * fountain alone is 35 across, the path round it is inset 22 from each edge,
 * and the dog needs `GRASS_MARGIN` of clearance inside the whole thing before
 * it will so much as squat.
 *
 * Exported so `validate.ts` can hold the finished level to the same floor.
 */
export const PARK_MIN = { w: 180, h: 160 };
